<template>
  <app-content class="new-report">
    <b-container fluid class="data-history-report">
      <b-row>
        <b-col>
          <reports-filter
            has-module-field
            is-company-field-required
            is-installation-field-required
            is-asset-field-required
            is-module-field-required
            :filter.sync="reportsFilter"
            :filter-objects.sync="reportsFilterObjects"
          />
        </b-col>
      </b-row>

      <b-row class="data-history-report__filter-buttons mb-5" align-h="end">
        <b-col class="mt-2" sm="12" md="auto">
          <b-button block @click="cleanAllFilters">
            {{ $t('general.clean-filters') }}
          </b-button>
        </b-col>
        <b-col class="mt-2 pl-0 pl-md-2" sm="12" md="auto">
          <b-button
            block
            variant="primary"
            @click="generateReport(getVariables)"
          >
            {{ $t('general.search') }}
          </b-button>
        </b-col>
      </b-row>

      <label>{{ $t('reports.data-history.research-items.variable') }}</label>
      <b-row align-v="center">
        <b-col class="mb-2" sm="4">
          <b-checkbox
            :checked="areAllVariablesFromPageSelected"
            :disabled="!(variables || []).length"
            @change="markAllVariablesFromPage"
          >
            <span class="font-weight-normal">
              {{ markAllCheckboxTitle(areAllVariablesFromPageSelected) }}
            </span>
          </b-checkbox>
        </b-col>
        <b-col class="mb-2 text-center" sm="4">
          <list-aggregator
            modal-item-id-key="assetModuleVariableId"
            modal-item-name-key="fullVariableDescription"
            configuration-mode
            configuration-label="reports.data-history.selected-variables"
            custom-modal-size="lg"
            is-paginated
            :per-page="pageSize"
            :modal-table-items.sync="selectedVariables"
            :modal-title="$t('reports.data-history.selected-variables-title')"
            :modal-table-header="
              $t('reports.data-history.selected-variables-title')
            "
            :is-html="true"
          />
        </b-col>
        <b-col class="mb-2" sm="4">
          <s-search-input
            v-model="genericSearch"
            v-s-trigger="onGenericSearch"
            :disabled="isSearchDisabled"
            :placeholder="
              $t('reports.data-history.research-items.search-variable')
            "
          />
        </b-col>
      </b-row>

      <data-history-variables-list
        class="mb-2"
        :variables="variables"
        :selected-variables="selectedVariables"
        :loading-variables="loadingVariables"
        @select-variable="selectVariable"
      />

      <grid-paginator
        v-model="page"
        :total-rows="totalItems"
        :per-page="pageSize"
        @input="paging"
      />

      <b-row>
        <b-col>
          <period-date-select
            class="data-history-report__period-select"
            :initialDate.sync="dataHistoryFilter.initialDate"
            :initialTime.sync="dataHistoryFilter.initialTime"
            :finalDate.sync="dataHistoryFilter.finalDate"
            :finalTime.sync="dataHistoryFilter.finalTime"
            :period.sync="dataHistoryFilter.period"
            @update:initialDate="validatePeriodFields($event, 'initialDate')"
            @update:initialTime="validatePeriodFields($event, 'initialTime')"
            @update:finalDate="validatePeriodFields($event, 'finalDate')"
            @update:finalTime="validatePeriodFields($event, 'finalTime')"
          />
        </b-col>
      </b-row>

      <report-footer-buttons
        :saveTemplateDisabled="saveTemplateLoading
        || !isPeriodValid
        || !isVariablesValid
        || !isPeriodRangeValid
        || !isVariableLimitValid"
        :viewReportDisabled="!isPeriodValid
        || !isVariablesValid
        || !isPeriodRangeValid
        || !isVariableLimitValid"
        :exportXlsxDisabled="exportXlsxLoading
        || !isVariablesValid
        || !isPeriodRangeValid
        || !isVariableLimitValid"
        :saveTemplateLoading="saveTemplateLoading"
        :exportXlsxLoading="exportXlsxLoading"
        @cancel="cancel"
        @save-template="openModal"
        @view-report="viewReport"
        @export-xlsx="exportXlsx"
      />
    </b-container>
    <save-template-modal
      v-model="showModal"
      :name.sync="dataHistoryFilter.name"
      :is-loading="saveTemplateLoading"
      @input="onTemplateModalInput"
      @save="saveHandler"
    />
  </app-content>
</template>
<script>
import uniq from 'lodash/uniq';
import LoadingMixin from '@/services/loadingMixin';
import { grammaticalJoin } from '@/services/arrayUtils';
import globalEventBus from '@/services/globalEventBus';
import { events } from '@/domain/services/generalPanel/reports/localEventBus';
import DataHistoryService from '@/domain/services/generalPanel/reports/dataHistory';
import periods from '@/domain/enums/periods';
import GenerateReportMixin from '@/domain/mixins/generalPanel/reports/generateReportMixin';
import PeriodDateSelect from '@/components/PeriodDateSelect';
import ReportsFilter from '@/components/ReportsFilter';
import GridPaginator from '@/components/lists/Paginator';
import ListAggregator from '@/components/listAggregator';
import ReportFooterButtons from '@/app/generalPanel/reports/ReportFooterButtons';
import DataHistoryVariablesList from '@/domain/components/generalPanel/reports/dataHistory/newReport/DataHistoryVariablesList';
import SaveTemplateModal from '@/domain/components/generalPanel/reports/dataHistory/newReport/summary/SaveTemplateModal';
import {
  formatter as dateTimeFormatter,
  joinISODateAndTimeString,
  setToStartOfDay,
  showDiff,
} from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import { mapGetters } from 'vuex';

export default {
  components: {
    GridPaginator,
    PeriodDateSelect,
    ReportsFilter,
    ListAggregator,
    ReportFooterButtons,
    SaveTemplateModal,
    DataHistoryVariablesList,
  },
  mixins: [LoadingMixin, GenerateReportMixin],
  data: () => ({
    dataHistoryFilter: {
      name: '',
      transient: null,
      period: periods.SEVEN,
    },
    reportsFilter: {},
    reportsFilterObjects: {},
    requiredFields: [
      'companiesIds',
      'installationsIds',
      'assetsIds',
      'modulesIds',
    ],
    emptyFields: [],
    modules: [],
    variables: [],
    selectedVariables: [],
    loadingModules: false,
    loadingVariables: false,
    genericSearch: '',
    page: 1,
    pageSize: 100,
    totalItems: null,
    showModal: false,
    reportLoader: false,
    exportXlsxLoading: false,
    saveTemplateLoading: false,
    templateIdResponse: '',
    originalTemplateName: null,
  }),
  computed: {
    ...mapGetters({
      limitVariablesHistoryData: 'limitVariablesHistoryData',
      limitHistoricalPeriodDataInDays: 'limitHistoricalPeriodDataInDays',
    }),
    disableExport() {
      return !this.isPeriodValid || this.loading;
    },
    isPeriodsUndefined() {
      return (
        this.dataHistoryFilter.period === periods.PERSONALIZED
        || this.dataHistoryFilter.period === undefined
      );
    },
    isDateFilled() {
      const {
        initialDate,
        finalDate,
        initialTime,
        finalTime,
      } = this.dataHistoryFilter;
      return initialDate && finalDate && initialTime && finalTime;
    },
    isPeriodValid() {
      return (
        this.isDateFilled
        || this.dataHistoryFilter.period !== periods.PERSONALIZED
      );
    },
    isPeriodRangeValid() {
      if (!this.isDateFilled) return true;

      const initialDate = joinISODateAndTimeString(
        this.dataHistoryFilter.initialDate,
        this.dataHistoryFilter.initialTime,
        { timeZone: timezones.UTC },
      );
      const finalDate = joinISODateAndTimeString(
        this.dataHistoryFilter.finalDate,
        this.dataHistoryFilter.finalTime,
        { timeZone: timezones.UTC },
      );
      const diffDate = showDiff(finalDate, initialDate, 'days');
      return diffDate.days <= this.limitHistoricalPeriodDataInDays;
    },
    selectedAssetsIds() {
      return this.reportsFilter.assetsIds || [];
    },
    selectedModulesIds() {
      return this.reportsFilter.modulesIds || [];
    },
    isSearchDisabled() {
      const {
        companiesIds,
        installationsIds,
        assetsIds,
        modulesIds,
      } = this.reportsFilter;
      return (
        !(companiesIds || []).length
        || !(installationsIds || []).length
        || !(assetsIds || []).length
        || !(modulesIds || []).length
        || this.loadingVariables
      );
    },
    templateId() {
      return this.$route?.params?.id || this.templateIdResponse;
    },
    isVariablesValid() {
      return !!(this.selectedVariables || []).length;
    },
    areAllVariablesFromPageSelected() {
      if (!this.isVariablesValid || !(this.variables || []).length) return false;

      const variablesIds = this.variables.map(
        ({ assetModuleVariableId }) => assetModuleVariableId,
      );

      const selectedVariablesFromPage = this.selectedVariables.filter(
        ({ assetModuleVariableId: id }) => variablesIds.includes(id),
      );

      return selectedVariablesFromPage.length === this.variables.length;
    },
    isVariableLimitValid() {
      return (this.selectedVariables || []).length <= this.limitVariablesHistoryData;
    },
  },
  created() {
    this.getTemplateIfEdit();
  },
  methods: {
    showPeriodValidateMessage() {
      this.$pushAlert({
        code: 'MA001',
        messageParams: {
          NomeDoCampo: grammaticalJoin(
            this.emptyFields.map(field => this.getFieldLabel(field)),
          ),
        },
      });
    },
    validatePeriodFields(value, field) {
      if (value) {
        this.emptyFields = this.emptyFields.filter(item => item !== field);
      }
      if (
        this.dataHistoryFilter.period !== undefined
        && this.isPeriodsUndefined
        && !value
        && !this.emptyFields.includes(field)
      ) {
        this.emptyFields.push(field);
        this.emptyFields = uniq(this.emptyFields);
        this.showPeriodValidateMessage();
        return;
      }

      if (this.isDateFilled) {
        this.validateDateRange();
      }
    },
    validateDateRange() {
      const initialDate = joinISODateAndTimeString(
        this.dataHistoryFilter.initialDate,
        this.dataHistoryFilter.initialTime,
        { timeZone: timezones.UTC },
      );
      const finalDate = joinISODateAndTimeString(
        this.dataHistoryFilter.finalDate,
        this.dataHistoryFilter.finalTime,
        { timeZone: timezones.UTC },
      );
      const diffDate = showDiff(finalDate, initialDate, 'days');
      if (diffDate.days > this.limitHistoricalPeriodDataInDays) {
        this.$pushAlert({
          code: 'ME091',
          messageParams: {
            0: this.limitHistoricalPeriodDataInDays,
          },
        });
      }
    },
    getFieldLabel(field) {
      const fields = {
        companiesIds: this.$t('entities.company'),
        installationsIds: this.$t('entities.installation'),
        assetsIds: this.$t('entities.asset'),
        modulesIds: this.$t('entities.module'),
        finalDate: this.$t('fields.general.final-date'),
        finalTime: this.$t('fields.general.final-time'),
        initialTime: this.$t('fields.general.initial-time'),
        initialDate: this.$t('fields.general.initial-date'),
      };
      return fields[field];
    },
    getVariables() {
      const { genericSearch, page, pageSize } = this;
      const assetsIds = this.selectedAssetsIds;
      const modulesIds = this.selectedModulesIds;
      this.loadingVariables = true;
      return DataHistoryService.getModuleVariables({
        assetsIds,
        modulesIds,
        genericSearch,
        page,
        pageSize,
      })
        .then((response) => {
          this.totalItems = response.total;
          this.variables = response.data;
        })
        .finally(() => {
          this.loadingVariables = false;
        });
    },
    cleanAllFilters() {
      globalEventBus.emit(events.CLEAR_REPORTS_FILTER);
      this.variables = [];
      this.totalItems = null;
    },
    paging(page) {
      this.currentPage = page;
      this.getVariables();
    },
    cancel() {
      this.$confirmMessage({ code: 'MC006' }).accepted(() => {
        this.$router.push({ name: 'reports.data-history' });
      });
    },
    getAllSelectedVariablesIds() {
      return [
        ...new Set(
          (this.selectedVariables || []).map(
            ({ assetModuleVariableId: id }) => id,
          ),
        ),
      ];
    },
    mountTemplateParams(transient, name) {
      this.dataHistoryFilter.transient = transient;
      this.dataHistoryFilter.variables = this.getAllSelectedVariablesIds();

      this.dataHistoryFilter.initialDateTime = joinISODateAndTimeString(
        this.dataHistoryFilter.initialDate,
        this.dataHistoryFilter.initialTime,
      );
      this.dataHistoryFilter.finalDateTime = joinISODateAndTimeString(
        this.dataHistoryFilter.finalDate,
        this.dataHistoryFilter.finalTime,
      );

      return {
        name,
        ...this.dataHistoryFilter,
      };
    },
    insertTemplate(transient = true, templateName = '') {
      const params = this.mountTemplateParams(transient, templateName);
      return DataHistoryService.insertTemplate(params);
    },
    editTemplate(templateId, templateName) {
      const params = this.mountTemplateParams(undefined, templateName);
      return DataHistoryService.updateTemplate(templateId, params);
    },
    saveTemplate() {
      return this.insertTemplate(false, this.dataHistoryFilter.name);
    },
    saveEditedTemplate() {
      return this.editTemplate(this.templateId, this.dataHistoryFilter.name);
    },
    saveHandler() {
      const callFunction = this.templateId
        ? this.saveEditedTemplate
        : this.saveTemplate;

      this.saveTemplateLoading = true;

      callFunction()
        .then(() => {
          this.$pushAlert({ code: 'MS006' });
          this.closeModal();
          this.goToList();
        })
        .finally(() => {
          this.saveTemplateLoading = false;
        });
    },
    goToReport(reportId) {
      const routeData = this.$router.resolve({
        name: 'clean-view.data-history.view-report',
        params: { reportId },
      });
      window.open(routeData.href, '_blank');
    },
    goToList() {
      const routeData = this.$router.resolve({
        name: 'reports.index',
      });
      window.open(routeData.href, '_self');
    },
    viewReport() {
      this.reportLoader = true;
      this.insertTemplate()
        .then((response) => {
          this.goToReport(response);
        })
        .finally(() => {
          this.reportLoader = false;
        });
    },
    async exportXlsx() {
      this.exportXlsxLoading = true;
      const templateResponse = await this.insertTemplate().finally(() => {
        this.exportXlsxLoading = false;
      });
      this.exportXlsxLoading = true;
      DataHistoryService.exportExcel(templateResponse).finally(() => {
        this.exportXlsxLoading = false;
      });
    },
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.onTemplateModalInput(false);
    },
    onTemplateModalInput(value) {
      this.showModal = value;

      if (!value && !this.saveTemplateLoading) {
        this.dataHistoryFilter.name = this.templateId ? this.originalTemplateName : '';
      }
    },
    splitDateTimes(response) {
      return {
        initialDate: setToStartOfDay(response.initialDateTime),
        initialTime: dateTimeFormatter(
          response.initialDateTime,
          formats.ISO_TIME,
        ),
        finalDate: setToStartOfDay(response.finalDateTime),
        finalTime: dateTimeFormatter(response.finalDateTime, formats.ISO_TIME),
      };
    },
    setFilterByTemplate(template) {
      this.originalTemplateName = template.name;
      this.dataHistoryFilter.name = template.name;
      this.reportsFilter.companiesIds = [
        ...new Set(template.variables?.map(x => x.companyId)),
      ];
      this.reportsFilter.regionalIds = [
        ...new Set(template.variables?.map(x => x.regionalId)),
      ];
      this.reportsFilter.installationsIds = [
        ...new Set(template.variables?.map(x => x.installationId)),
      ];
      this.reportsFilter.assetsIds = [
        ...new Set(template.variables?.map(x => x.assetId)),
      ];
      this.reportsFilter.modulesIds = [
        ...new Set(template.variables?.map(x => x.moduleId)),
      ];

      this.reportsFilterObjects.companies = this.reportsFilter.companiesIds.map((id) => {
        const { companyName } = template.variables.find(x => x.companyId === id);
        return { id, name: companyName };
      });

      this.reportsFilterObjects.regional = this.reportsFilter.regionalIds.map((id) => {
        const {
          regionalName,
          companyId,
          companyName,
        } = template.variables.find(x => x.regionalId === id);

        return {
          id,
          name: regionalName,
          company: { id: companyId, name: companyName },
        };
      });

      this.reportsFilterObjects.installations = this.reportsFilter.installationsIds.map((id) => {
        const {
          installationName,
          regionalId,
          regionalName,
          companyId,
          companyName,
        } = template.variables.find(x => x.installationId === id);
        return {
          id,
          name: installationName,
          regional: { id: regionalId, name: regionalName },
          company: { id: companyId, name: companyName },
        };
      });

      this.reportsFilterObjects.assets = this.reportsFilter.assetsIds.map((id) => {
        const {
          assetName,
          installationName,
          installationId,
        } = template.variables.find(x => x.assetId === id);

        return {
          id,
          name: `${installationName} | ${assetName}`,
          installation: { id: installationId, name: installationName },
        };
      });

      this.reportsFilterObjects.modules = this.reportsFilter.modulesIds.map((id) => {
        const { moduleName } = template.variables.find(x => x.moduleId === id);
        const assetsIds = template.variables.filter(x => x.moduleId === id).map(x => x.assetId);
        return {
          id,
          name: moduleName,
          assetsIds,
        };
      });

      const hasCustomDates = !!(template.initialDateTime && template.finalDateTime);

      this.dataHistoryFilter = {
        ...template,
        ...this.splitDateTimes(template),
        variables: this.getAllSelectedVariablesIds(),
        period: hasCustomDates ? periods.PERSONALIZED : template.period,
      };

      this.onGenericSearch();
    },
    getTemplateIfEdit() {
      if (!this.templateId) return;
      this.startLoading();
      DataHistoryService.getTemplate(this.templateId)
        .then((response) => {
          if (response.partial) {
            this.$pushAlert({ code: 'MI106' });
          }
          this.selectedVariables = response.variables || [];
          this.setFilterByTemplate(response);
        })
        .finally(() => {
          this.endLoading();
        });
    },
    onGenericSearch() {
      this.generateReport(this.getVariables);
    },
    markAllCheckboxTitle(state) {
      if (state) {
        return this.$t('reports.data-history.remove-all-variables');
      }

      return this.$t('reports.data-history.select-all-variables');
    },
    markAllVariablesFromPage(state) {
      if (!(this.variables || []).length) return;

      const variablesIds = this.variables.map(
        ({ assetModuleVariableId }) => assetModuleVariableId,
      );

      this.selectedVariables = (this.selectedVariables || []).filter(
        ({ assetModuleVariableId: id }) => !variablesIds.includes(id),
      );

      if (state) {
        this.selectedVariables = [...this.selectedVariables, ...this.variables];
      }
    },
    selectVariable(variable) {
      if (!Array.isArray(this.selectedVariables)) {
        this.selectedVariables = [];
      }

      const selectedVariablesIds = this.selectedVariables.map(
        ({ assetModuleVariableId }) => assetModuleVariableId,
      );

      if (!selectedVariablesIds.includes(variable.assetModuleVariableId)) {
        this.selectedVariables.push(variable);
        return;
      }

      const { assetModuleVariableId } = variable;
      this.selectedVariables = this.selectedVariables.filter(
        ({ assetModuleVariableId: id }) => id !== assetModuleVariableId,
      );
    },
  },
  watch: {
    selectedVariables(newValue) {
      if (newValue.length > this.limitVariablesHistoryData) {
        this.$pushAlert({
          code: 'ME090',
          messageParams: {
            0: this.limitVariablesHistoryData,
          },
        });
      }
    },
  },
};
</script>
<style lang="sass" scoped>
.data-history-report
  &__period-select
    margin-top: 15px

  &__xlsx-loading
    display: inline-flex

  ::v-deep
    .input-group.search
      height: auto
</style>
<style lang="sass">
.fullVariableDescriptionHtml
  color: var(--color-gray-intermediary-darker)
</style>
